import {
  isValidPhoneNumber,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";
import { z } from "zod";
import type { SiteFormErrors } from "@/content/schema";
import {
  isPhoneCountryIso,
  type ContactCurrency,
} from "@/lib/phone-countries";

export type { ContactCurrency };
export type PhoneCountryIso = CountryCode;

export {
  currencyForCountry,
  detectPhoneCountryIso,
} from "@/lib/phone-countries";

/** Comma-group digit runs only — letters / ranges / “k” stay intact.
 * Strip existing commas first so re-typing doesn’t format each chunk alone
 * (e.g. "4,000" + "0" → "4,0000" must become "40,000", not "4,0,000"). */
export function formatBudgetInput(raw: string): string {
  return raw.replace(/[\d,]+/g, (chunk) => {
    const digits = chunk.replace(/,/g, "");
    if (!digits) return chunk;
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  });
}

/** Map caret after reformatting digit groups with commas. */
export function formatBudgetWithCaret(
  raw: string,
  caret: number,
): { value: string; caret: number } {
  const before = raw.slice(0, Math.max(0, caret));
  const value = formatBudgetInput(raw);
  return { value, caret: formatBudgetInput(before).length };
}

/** SSR-safe default; client should call `detectPhoneCountryIso()` after mount. */
export function defaultPhoneCountryIso(): PhoneCountryIso {
  return "EG";
}

/** Build E.164 from ISO country + national number (libphonenumber-js). */
export function toE164Phone(
  iso: string,
  national: string,
): string | null {
  const trimmed = national.trim();
  if (!trimmed) return null;
  try {
    const parsed = parsePhoneNumberFromString(trimmed, iso as CountryCode);
    if (parsed?.isValid()) return parsed.format("E.164");
  } catch {
    /* fall through */
  }
  try {
    const parsed = parsePhoneNumberFromString(trimmed);
    if (parsed?.isValid()) return parsed.format("E.164");
  } catch {
    /* ignore */
  }
  return null;
}

export function isValidPhoneForCountry(iso: string, national: string): boolean {
  const trimmed = national.trim();
  if (!trimmed) return false;
  try {
    if (isValidPhoneNumber(trimmed, iso as CountryCode)) return true;
  } catch {
    /* ignore */
  }
  try {
    return isValidPhoneNumber(trimmed);
  } catch {
    return false;
  }
}

export function contactFormSchema(errors: SiteFormErrors) {
  return z
    .object({
      name: z.string().trim().min(1, errors.nameRequired),
      business: z.string().trim(),
      phoneCountry: z
        .string()
        .refine(isPhoneCountryIso, errors.phoneCountry),
      phone: z.string().trim().min(1, errors.phoneRequired),
      email: z
        .string()
        .trim()
        .refine(
          (value) => value === "" || z.email().safeParse(value).success,
          errors.emailInvalid,
        ),
      budget: z.string().trim(),
      currency: z.enum(["EGP", "USD"], { message: errors.currencyRequired }),
      details: z.string().trim().min(1, errors.detailsRequired),
    })
    .superRefine((data, ctx) => {
      if (!isValidPhoneForCountry(data.phoneCountry, data.phone)) {
        ctx.addIssue({
          code: "custom",
          path: ["phone"],
          message: errors.phoneInvalid,
        });
      }
    })
    .transform((data) => {
      const e164 = toE164Phone(data.phoneCountry, data.phone) ?? data.phone;
      return {
        name: data.name,
        business: data.business,
        phoneCountry: data.phoneCountry,
        phone: e164,
        email: data.email,
        budget: data.budget,
        currency: data.currency,
        details: data.details,
      };
    });
}

export type ContactFormValues = z.infer<ReturnType<typeof contactFormSchema>>;
export type ContactFormErrors = Partial<
  Record<keyof ContactFormValues | "phoneCountry", string>
>;

export function contactFormErrorsFromZod(
  error: z.ZodError,
): ContactFormErrors {
  const { fieldErrors } = z.flattenError(error) as {
    fieldErrors: Partial<Record<string, string[]>>;
  };
  const out: ContactFormErrors = {};
  for (const key of Object.keys(fieldErrors)) {
    const messages = fieldErrors[key];
    if (messages?.[0]) {
      out[key as keyof ContactFormErrors] = messages[0];
    }
  }
  return out;
}
